// ============================================================================
// FILE: src/modules/ticket/pages/performance/PerformanceDashboardPage.jsx
// ============================================================================

import React, { useState } from "react";
import usePerformanceDashboard from "../../hooks/usePerformanceDashboard";
import PerformanceFilterBar from "../../components/performance/PerformanceFilterBar";

// ✅ STEP C6 – Charts
import SLATrendChart from "../../components/performance/charts/SLATrendChart";
import TicketVolumeChart from "../../components/performance/charts/TicketVolumeChart";
import MTTRTrendChart from "../../components/performance/charts/MTTRTrendChart";

// ✅ Ticket list for drill-down
import TicketList from "../TicketList";

// ------------------------------------------------------------------
const SHOW_DEBUG_CONTEXT = false;

// ------------------------------------------------------------------
const Header = () => (
  <div style={{ marginBottom: 16 }}>
    <h2 style={{ marginBottom: 4 }}>Ticket Performance Dashboard</h2>
    <div style={{ fontSize: 13, color: "#666" }}>
      SLA compliance, efficiency, and operational workload overview
    </div>
  </div>
);

// ------------------------------------------------------------------
const Tabs = ({ activeTab, onChange }) => (
  <div style={{ marginBottom: 16 }}>
    {["tenant", "location", "executive"].map((tab) => (
      <button
        key={tab}
        onClick={() => onChange(tab)}
        style={{
          marginRight: 8,
          padding: "6px 12px",
          borderRadius: 6,
          border: "1px solid #ccc",
          background: activeTab === tab ? "#2563eb" : "#fff",
          color: activeTab === tab ? "#fff" : "#000",
          fontWeight: 600,
        }}
      >
        {tab.charAt(0).toUpperCase() + tab.slice(1)}
      </button>
    ))}
  </div>
);

// ------------------------------------------------------------------
const formatSeconds = (seconds) => {
  const sec = Number(seconds) || 0;
  if (sec <= 0) return "0m";
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

// ------------------------------------------------------------------
const DrilldownDrawer = ({ open, title, context, onClose }) => {
  if (!open) return null;

  const ticketFilter = context?.type === "executive" ? "my" : "all";

  const getFriendlyTitle = () => {
    if (!context) return title;

    if (context.type === "kpi") {
      const map = {
        tickets_created: "Tickets Created",
        tickets_closed: "Tickets Closed",
        sla_met: "SLA Met Tickets",
        sla_breached: "SLA Breached Tickets",
        avg_mttr: "MTTR Related Tickets",
        escalation_ratio: "Escalated Tickets",
      };
      return map[context.key] || "KPI Drill-down";
    }

    if (context.type === "location") {
      return `Location: ${context.name || context.id}`;
    }

    if (context.type === "executive") {
      return `Executive: ${context.name || context.id}`;
    }

    return title;
  };

  const getContextSummary = () => {
    if (!context) return null;

    if (context.type === "kpi") {
      return `Showing tickets related to selected KPI for the chosen period.`;
    }

    if (context.type === "location") {
      return `Showing tickets for the selected location.`;
    }

    if (context.type === "executive") {
      return `Showing tickets assigned to the selected executive.`;
    }

    return null;
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        right: 0,
        height: "100vh",
        width: "40%",
        background: "#fff",
        boxShadow: "-6px 0 18px rgba(0,0,0,0.15)",
        zIndex: 9999,
        padding: 20,
        overflowY: "auto",
      }}
    >
      <div style={{ marginBottom: 16 }}>
        <strong>{getFriendlyTitle()}</strong>
        <button onClick={onClose} style={{ float: "right", cursor: "pointer" }}>
          ✕
        </button>
      </div>

      {getContextSummary() && (
        <div style={{ fontSize: 13, color: "#555", marginBottom: 12 }}>
          {getContextSummary()}
        </div>
      )}

      {SHOW_DEBUG_CONTEXT && (
        <div style={{ fontSize: 14, color: "#555", marginBottom: 16 }}>
          <p><strong>Context (debug):</strong></p>
          <pre style={{ background: "#f9fafb", padding: 12, borderRadius: 6 }}>
{JSON.stringify(context, null, 2)}
          </pre>
        </div>
      )}

      <div style={{ marginTop: 16 }}>
        <h4 style={{ marginBottom: 12 }}>Tickets</h4>
        <TicketList filter={ticketFilter} context={context} />
      </div>
    </div>
  );
};

// ------------------------------------------------------------------
const GradientKpiCard = ({ label, value, gradient, comparison, onClick }) => (
  <div
    onClick={onClick}
    style={{
      minWidth: 200,
      padding: 18,
      borderRadius: 16,
      color: "#fff",
      background: gradient,
      boxShadow: "0 6px 16px rgba(0,0,0,0.12)",
      cursor: onClick ? "pointer" : "default",
    }}
  >
    <div style={{ fontSize: 13, opacity: 0.9 }}>{label}</div>
    <div style={{ fontSize: 28, fontWeight: 800, marginTop: 4 }}>{value}</div>
  </div>
);

// --------------------
// EVERYTHING BELOW IS 100% UNCHANGED
// --------------------

// --------------------
// EVERYTHING BELOW IS 100% UNCHANGED
// --------------------
const ValidationBanner = ({ data }) => {
  if (!data) return null;

  const slaMet = Number(data.sla_met_pct ?? 0);
  const slaBreached = Number(data.sla_breached_pct ?? 0);
  const slaSum = slaMet + slaBreached;

  const escalationRatio = Number(data.escalation_ratio ?? 0);
  const ticketsClosed = Number(data.tickets_closed ?? 0);
  const avgMttr = Number(data.avg_mttr_seconds ?? 0);

  const warnings = [];

  if (slaSum < 98 || slaSum > 102) {
    warnings.push(
      `SLA Met (${slaMet.toFixed(1)}%) + SLA Breached (${slaBreached.toFixed(
        1
      )}%) does not equal ~100%.`
    );
  }

  if (escalationRatio > 0.8) {
    warnings.push(
      `High escalation ratio (${escalationRatio.toFixed(
        2
      )}). Review escalation or assignment process.`
    );
  }

  if (ticketsClosed > 0 && avgMttr === 0) {
    warnings.push(
      `Tickets are closed but MTTR is reported as 0. Resolution timestamps may be missing.`
    );
  }

  if (warnings.length === 0) return null;

  return (
    <div
      style={{
        marginTop: 16,
        marginBottom: 16,
        padding: 14,
        borderRadius: 8,
        background: "#fffbeb",
        border: "1px solid #f59e0b",
        color: "#92400e",
        fontSize: 13,
      }}
    >
      <strong>Data Integrity Notice</strong>
      <ul style={{ marginTop: 6, paddingLeft: 18 }}>
        {warnings.map((w, i) => (
          <li key={i}>{w}</li>
        ))}
      </ul>
    </div>
  );
};

// ------------------------------------------------------------------
const TenantKpis = ({ data, comparison, onDrill }) => {
  if (!data) return <p>No tenant data available.</p>;

  return (
    <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 24 }}>
      <GradientKpiCard
        label="Tickets Created"
        value={Number(data.tickets_created ?? 0)}
        gradient="linear-gradient(135deg,#2563eb,#3b82f6)"
        comparison={comparison?.totalTickets}
        onClick={() => onDrill("kpi", { key: "tickets_created" })}
      />
      <GradientKpiCard
        label="Tickets Closed"
        value={Number(data.tickets_closed ?? 0)}
        gradient="linear-gradient(135deg,#15803d,#22c55e)"
        onClick={() => onDrill("kpi", { key: "tickets_closed" })}
      />
      <GradientKpiCard
        label="SLA Met (%)"
        value={`${Number(data.sla_met_pct ?? 0).toFixed(1)}%`}
        gradient="linear-gradient(135deg,#166534,#22c55e)"
        comparison={comparison?.slaPercentage}
        onClick={() => onDrill("kpi", { key: "sla_met" })}
      />
      <GradientKpiCard
        label="SLA Breached (%)"
        value={`${Number(data.sla_breached_pct ?? 0).toFixed(1)}%`}
        gradient="linear-gradient(135deg,#b91c1c,#ef4444)"
        onClick={() => onDrill("kpi", { key: "sla_breached" })}
      />
      <GradientKpiCard
        label="Avg MTTR"
        value={formatSeconds(data.avg_mttr_seconds)}
        gradient="linear-gradient(135deg,#1e3a8a,#f59e0b)"
        comparison={comparison?.mttr}
        onClick={() => onDrill("kpi", { key: "avg_mttr" })}
      />
      <GradientKpiCard
        label="Escalation Ratio"
        value={Number(data.escalation_ratio ?? 0).toFixed(2)}
        gradient="linear-gradient(135deg,#6b21a8,#dc2626)"
        comparison={comparison?.escalations}
        onClick={() => onDrill("kpi", { key: "escalation_ratio" })}
      />
    </div>
  );
};

// ------------------------------------------------------------------
const LocationTable = ({ data, onDrill }) => {
  if (!Array.isArray(data) || data.length === 0) {
    return <p>No location data available.</p>;
  }

  return (
    <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 16 }}>
      <thead>
        <tr>
          <th align="left">Location</th>
          <th align="right">Tickets</th>
          <th align="right">SLA Met %</th>
          <th align="right">SLA Breached %</th>
          <th align="right">Avg MTTR</th>
          <th align="right">Escalations</th>
        </tr>
      </thead>
      <tbody>
        {data.map((row, idx) => (
          <tr
            key={row.location_id}
            onClick={() =>
              onDrill("location", {
                id: row.location_id,
                name: row.location_name || row.location_code,
              })
            }
            style={{
              background: idx % 2 === 0 ? "#fafafa" : "#fff",
              cursor: "pointer",
            }}
          >
            <td>{row.location_name || `Location ${row.location_id}`}</td>
            <td align="right">{row.tickets}</td>
            <td align="right">
              {((row.sla_met / row.tickets) * 100 || 0).toFixed(1)}%
            </td>
            <td align="right">
              {((row.sla_breached / row.tickets) * 100 || 0).toFixed(1)}%
            </td>
            <td align="right">{formatSeconds(row.avg_mttr)}</td>
            <td align="right">{row.escalations}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

// ------------------------------------------------------------------
const ExecutiveTable = ({ data, onDrill }) => {
  if (!Array.isArray(data) || data.length === 0) {
    return <p>No executive performance data available.</p>;
  }

  return (
    <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 16 }}>
      <thead>
        <tr>
          <th align="left">Executive</th>
          <th align="right">Tickets</th>
          <th align="right">SLA Met %</th>
          <th align="right">SLA Breached %</th>
          <th align="right">Avg MTTR</th>
          <th align="right">Avg MTTA</th>
          <th align="right">Reopens</th>
          <th align="right">Escalations</th>
        </tr>
      </thead>
      <tbody>
        {data.map((row, idx) => (
          <tr
            key={row.executive_id}
            onClick={() =>
              onDrill("executive", {
                id: row.executive_id,
                name: row.executive_name || row.user_name,
              })
            }
            style={{
              background: idx % 2 === 0 ? "#fafafa" : "#fff",
              cursor: "pointer",
            }}
          >
            <td>{row.executive_name || `Executive ${row.executive_id}`}</td>
            <td align="right">{row.tickets}</td>
            <td align="right">
              {((row.sla_met / row.tickets) * 100 || 0).toFixed(1)}%
            </td>
            <td align="right">
              {((row.sla_breached / row.tickets) * 100 || 0).toFixed(1)}%
            </td>
            <td align="right">{formatSeconds(row.avg_mttr)}</td>
            <td align="right">{formatSeconds(row.avg_mtta)}</td>
            <td align="right">{row.reopens}</td>
            <td align="right">{row.escalations}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

// ------------------------------------------------------------------
export default function PerformanceDashboardPage() {
  const {
    activeTab,
    setActiveTab,
    data,
    comparisonData,
    filters,
    updateFilters,
    resetFilters,
    loading,
    error,
    refresh,
  } = usePerformanceDashboard();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerTitle, setDrawerTitle] = useState("");
  const [drawerContext, setDrawerContext] = useState(null);

  const handleDrill = (type, payload) => {
    setDrawerTitle(`Drill-down: ${type}`);
    setDrawerContext({ type, ...payload });
    setDrawerOpen(true);
  };

  const tenantData = data?.tenant ?? data;
  const locationData = data?.locations ?? data;
  const executiveData = data?.executives ?? data;

  return (
    <div className="ticket-performance-dashboard">
      <Header />
      <Tabs activeTab={activeTab} onChange={setActiveTab} />
      <PerformanceFilterBar
        filters={filters}
        onChange={updateFilters}
        onReset={resetFilters}
      />

      {loading && <p>Loading performance data...</p>}
      {error && <p style={{ color: "red" }}>{error}</p>}

      {!loading && !error && activeTab === "tenant" && (
        <>
          <TenantKpis
            data={tenantData}
            comparison={comparisonData}
            onDrill={handleDrill}
          />

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            <SLATrendChart slaPct={tenantData?.sla_met_pct} />
            <ValidationBanner data={tenantData} />
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 16,
              marginTop: 16,
            }}
          >
            <TicketVolumeChart
              created={tenantData?.tickets_created}
              closed={tenantData?.tickets_closed}
            />
            <MTTRTrendChart
              mttr={tenantData?.avg_mttr_seconds}
              mtta={tenantData?.avg_mtta_seconds}
            />
          </div>
        </>
      )}

      {!loading && !error && activeTab === "location" && (
        <LocationTable data={locationData} onDrill={handleDrill} />
      )}

      {!loading && !error && activeTab === "executive" && (
        <ExecutiveTable data={executiveData} onDrill={handleDrill} />
      )}

      <div style={{ marginTop: 16 }}>
        <button onClick={refresh}>Refresh</button>
      </div>

      <DrilldownDrawer
        open={drawerOpen}
        title={drawerTitle}
        context={drawerContext}
        onClose={() => setDrawerOpen(false)}
      />
    </div>
  );
}
